import React, { useState } from 'react';
import { toast } from 'sonner';
import { eventAPI } from '../../lib/eventApi';
import { X, Trash, Warning } from '@phosphor-icons/react';

const CONFIRM_WORD = 'DELETE';

/**
 * Delete Session dialog.
 *
 *  • Allowed only on pre-start sessions (draft / registration_open /
 *    registration_closed). The Edit button entry-point is already gated;
 *    this component is rendered conditionally by the parent.
 *  • Admin must type "DELETE" exactly to enable the destructive button.
 *  • Backend cascades all child rows in safe FK order.
 */
export default function DeleteSessionDialog({ open, session, onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  if (!open || !session) return null;

  const canConfirm = confirmText === CONFIRM_WORD && !deleting;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await eventAPI.deleteSession(session.id);
      toast.success('Session deleted');
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
      setDeleting(false);
    }
  };

  const handleClose = () => {
    if (deleting) return;
    setConfirmText('');
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40 p-2 sm:p-4"
      data-testid="delete-session-dialog"
    >
      <div className="w-full sm:max-w-md bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-[#e3dcd1]">
          <h2 className="font-serif text-2xl text-[#013523]">Delete session</h2>
          <button
            type="button"
            onClick={handleClose}
            data-testid="delete-session-dialog-close"
            className="text-[#6a766e] hover:text-[#013523] p-1 rounded-lg"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
            <Warning size={18} weight="fill" className="mt-0.5 shrink-0 text-red-700" />
            <div className="text-xs text-red-900">
              <div className="font-medium">This cannot be undone.</div>
              <div className="mt-1">
                Deleting <span className="font-medium">"{session.name}"</span> removes all of its registrations, weeks, court data, and audit history. Singles ladder, profiles, and challenges are not affected.
              </div>
            </div>
          </div>

          <label className="block space-y-1">
            <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">
              Type <span className="font-mono font-semibold text-[#013523]">{CONFIRM_WORD}</span> to confirm
            </div>
            <input
              data-testid="delete-confirm-input"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_WORD}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              className="w-full px-3 py-2 rounded-lg border border-[#e3dcd1] bg-white text-sm font-mono"
            />
          </label>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={deleting}
              data-testid="delete-session-cancel"
              className="flex-1 px-3 py-2 rounded-lg border border-[#e3dcd1] text-sm text-[#013523] hover:bg-[#f0ece3] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={!canConfirm}
              data-testid="delete-session-confirm"
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash size={14} weight="bold" /> {deleting ? 'Deleting…' : 'Delete session'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
